import { lazy, Suspense, useMemo } from 'react'
import type { Locale, MapDTO } from '../../shared/contracts'
import { t } from '../i18n'

const ProfileMap3D = lazy(() => import('./ProfileMap3D'))

const colors: Record<string, string> = {
  interests: '#65d7a7', aesthetics: '#d59cff', values: '#ff8e78', skills: '#72b8ff', goals: '#ffd36e',
  learning: '#7ce3e8', work: '#94a6ff', social: '#ff9bc8', experiences: '#b9d66f',
}

function normalizeMap(map: MapDTO) {
  if (!map.nodes.length) return { nodes: [], edges: [] }
  const extent = Math.max(1, ...map.nodes.flatMap((node) => [Math.abs(node.x), Math.abs(node.y), Math.abs(node.z)]))
  const nodes = map.nodes.map((node) => ({ ...node, x: node.x / extent * 88, y: node.y / extent * 88, z: node.z / extent * 88 }))
  return { nodes, edges: map.edges }
}

export function ProfileMap({ map, locale }: { map: MapDTO; locale: Locale }) {
  const copy = t(locale)
  const normalized = useMemo(() => normalizeMap(map), [map])
  if (map.dimensions === 3) {
    return <Suspense fallback={<div className="map-loading">{copy.mapLoading}</div>}><ProfileMap3D map={{ ...map, ...normalized }} colors={colors} /></Suspense>
  }
  const lookup = new Map(normalized.nodes.map((node) => [node.id, node]))
  return (
    <svg className="profile-map" viewBox="-105 -105 210 210" role="img" aria-label={copy.mapAria}>
      <defs>
        <radialGradient id="mapGlow"><stop offset="0" stopColor="#72f2bd" stopOpacity=".35"/><stop offset="1" stopColor="#72f2bd" stopOpacity="0"/></radialGradient>
      </defs>
      <circle cx="0" cy="0" r="98" fill="url(#mapGlow)" opacity=".22" />
      <g className="map-edges">
        {normalized.edges.map((edge) => {
          const source = lookup.get(edge.source); const target = lookup.get(edge.target)
          if (!source || !target) return null
          return <line key={`${edge.source}-${edge.target}`} x1={source.x} y1={source.y} x2={target.x} y2={target.y} opacity={Math.max(.08, edge.weight * .32)} />
        })}
      </g>
      <g>
        {normalized.nodes.map((node) => (
          <g key={node.id} transform={`translate(${node.x} ${node.y})`} className={node.confirmed ? 'map-node confirmed' : 'map-node backdrop'}>
            <circle r={node.confirmed ? 3.7 + Math.abs(node.value ?? 0) : 1.45} fill={colors[node.domain] ?? '#a2b6ad'} />
            {node.confirmed && <text y="-7" textAnchor="middle">{node.label}</text>}
            <title>{node.label}</title>
          </g>
        ))}
      </g>
    </svg>
  )
}
